import { useEffect, useState } from 'react';

/**
 * Reads a JSON file the build wrote under /data/ (scripts/prerender.mjs) —
 * the same records the static HTML was generated from, so a page built on
 * it says exactly what its prerendered version said.
 *
 * state: 'loading' | 'ok' | 'missing' (no such file in this build) |
 * 'error' (the request itself failed). Only 'missing' is a real answer; an
 * 'error' must never be read as "this page does not exist".
 */
export function useBuildData(path) {
    const [result, setResult] = useState({ state: 'loading', data: null, path });

    useEffect(() => {
        if (!path) return undefined;
        let cancelled = false;
        fetch(`/data/${path}`)
            .then(async (r) => {
                // An unknown path falls through to the SPA shell (HTML, 200)
                // or a 404: either way the file is not in this build.
                const isJson = (r.headers.get('content-type') || '').includes('json');
                if (!r.ok || !isJson) return { state: r.status >= 500 ? 'error' : 'missing', data: null };
                return { state: 'ok', data: await r.json() };
            })
            .catch(() => ({ state: 'error', data: null }))
            .then((next) => { if (!cancelled) setResult({ ...next, path }); });
        return () => { cancelled = true; };
    }, [path]);

    // A path change shows 'loading' until its own result arrives.
    return result.path === path ? result : { state: 'loading', data: null, path };
}
